import { type RowData } from "@tanstack/react-table";
import { z } from "zod";
import { replaceEqualDeep } from "@tanstack/react-query";
import { type AppTable } from "../../../components/table/tableFeatures";
import type { DataView } from "../../../zodios/apiDefinitions/api/dataViews";
import type { ServerColumnFilter } from "../../../zodios/common/columnFilterSchema";
import { FILTER_IDS_BLACKLIST } from "../../merchandising/components/tables/filters";

export const TABLE_VIEW_TYPE = "table";

const serverColumnFilterItemSchema = z.object({
  id: z.string(),
  value: z.object({
    op: z.string(),
    value: z.unknown(),
  }),
});

const sortingItemSchema = z.object({
  id: z.string(),
  desc: z.boolean(),
});

const tableDataViewSchema = z.object({
  fields: z.array(z.string()).default([]),
  filters: z.array(serverColumnFilterItemSchema).default([]),
  sorting: z.array(sortingItemSchema).default([]),
  grouping: z.array(z.string()).default([]),
  search: z.string().default(""),
});

type TableDataViewState = z.infer<typeof tableDataViewSchema>;

export const parseTableDataView = (view: DataView): TableDataViewState =>
  tableDataViewSchema.parse({
    fields: view.fields,
    filters: view.filters,
    sorting: view.sorting,
    grouping: view.grouping,
    search: view.search,
  });

const blacklistSet = new Set<string>(FILTER_IDS_BLACKLIST);
const stripBlacklisted = (filters: readonly ServerColumnFilter[]): ServerColumnFilter[] =>
  filters.filter((f) => !blacklistSet.has(f.id));

export const readTableStateFromTable = <TData extends RowData>(table: AppTable<TData>): TableDataViewState => {
  const state = table.state;
  const leafColumns = table.getAllLeafColumns();
  const orderedIds = state.columnOrder.length > 0 ? state.columnOrder : leafColumns.map((c) => c.id);
  const knownIds = new Set(leafColumns.map((c) => c.id));

  const fields = orderedIds.filter((id) => {
    if (!knownIds.has(id)) return false;
    const col = table.getColumn(id);
    return col?.getIsVisible() ?? false;
  });

  return {
    fields,
    filters: stripBlacklisted(state.columnFilters as ServerColumnFilter[]),
    sorting: state.sorting,
    grouping: state.grouping,
    search: state.globalFilter ?? "",
  };
};

export const applyTableStateToTable = <TData extends RowData>(
  table: AppTable<TData>,
  viewState: TableDataViewState,
) => {
  const leafColumns = table.getAllLeafColumns();
  const visibleSet = new Set(viewState.fields);

  const nextVisibility: Record<string, boolean> = {};
  for (const col of leafColumns) {
    nextVisibility[col.id] = col.getCanHide() ? visibleSet.has(col.id) : col.getIsVisible();
  }

  table.setColumnVisibility(nextVisibility);
  table.setColumnOrder(viewState.fields);
  table.setColumnFilters(stripBlacklisted(viewState.filters));
  table.setSorting(viewState.sorting);
  table.setGrouping(viewState.grouping);
  table.setGlobalFilter(viewState.search);
  table.resetColumnPinning();
};

export const resetTableStateToDefaults = <TData extends RowData>(table: AppTable<TData>) => {
  table.resetColumnVisibility();
  table.resetColumnOrder();
  table.resetColumnFilters();
  table.resetSorting();
  table.resetGrouping();
  table.resetGlobalFilter();
  table.resetColumnPinning();
};

export const isTableViewDirty = (current: TableDataViewState, saved: TableDataViewState): boolean =>
  replaceEqualDeep(saved, current) !== saved;
